import { useEffect, useMemo, useState } from "react";
import styles from "./CreateCompanyModal.module.css";
import { API_URL } from "../../../config/api";

const initialFormState = {
  type: "",
  name: "",
  sn: "",
  category_id: "",
  specialization_id: "",

  address: {
    city: "",
    street: "",
    building: "",
    office: "",
    full: "",
  },

  tax_regime: "",
  is_vat_payer: "",
  vat_series: "",
  reg_number: "",
  reg_date: "",

  owner_name: "",
  owner_surname: "",
  owner_patronymic: "",
  owner_sn: "",
  owner_phone: "",
  owner_email: "",
  password: "",
};

export default function CreateCompanyModal({ open, onClose, onCreated }) {
  const token = localStorage.getItem("token");

  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState("");
  const [form, setForm] = useState(initialFormState);

  const [categories, setCategories] = useState([]);
  const [specializations, setSpecializations] = useState([]);

  // уникальные значения для autocomplete, чтобы браузер не понимал что это за поля
  const ac = useMemo(() => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return {
      form: `off-${id}`,
      type: `nope-type-${id}`,
      name: `nope-company-name-${id}`,
      sn: `nope-sn-${id}`,
      category: `nope-category-${id}`,
      spec: `nope-spec-${id}`,

      city: `nope-city-${id}`,
      street: `nope-street-${id}`,
      building: `nope-building-${id}`,
      office: `nope-office-${id}`,

      tax: `nope-tax-${id}`,
      vat: `nope-vat-${id}`,
      vat_series: `nope-vat-series-${id}`,
      reg_number: `nope-reg-number-${id}`,

      owner_name: `nope-owner-name-${id}`,
      owner_surname: `nope-owner-surname-${id}`,
      owner_patronymic: `nope-owner-patronymic-${id}`,
      owner_sn: `nope-owner-sn-${id}`,
      owner_phone: `nope-owner-phone-${id}`,
      owner_email: `nope-owner-email-${id}`,
      password: `new-password`,
    };
  }, []);

  const resetForm = () => {
    setForm(initialFormState);
    setErrors({});
    setToast("");
    setSpecializations([]);
  };

  useEffect(() => {
    if (!open) return;

    // когда открыли модалку — сброс (чтобы старые значения не всплывали)
    resetForm();

    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/org_categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCategories(data.data || []);
      } catch (err) {
        console.error("Ошибка загрузки категорий:", err);
      }
    };

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!form.category_id) return;

    const fetchSpecializations = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/org_specializations?category_id=${form.category_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setSpecializations(data.data || []);
      } catch (err) {
        console.error("Ошибка загрузки специализаций:", err);
      }
    };

    fetchSpecializations();
  }, [form.category_id, token]);

  const handleChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;

    if (name === "is_vat_payer") {
      // select отдаёт строку, превращаем в boolean или ""
      value = value === "" ? "" : value === "true";
    }

    if (name === "name") {
      const removePrefixes = [
        /^тoo\s*/i,
        /^тоо\s*/i,
        /^ip\s*/i,
        /^ип\s*/i,
        /^ao\s*/i,
        /^ао\s*/i,
        /^ooo\s*/i,
        /^ооо\s*/i,
      ];
      removePrefixes.forEach((regex) => {
        value = value.replace(regex, "");
      });
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" ? { specialization_id: "" } : {}),
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    const newAddress = {
      ...form.address,
      [name]: value,
    };

    const parts = [];
    if (newAddress.city) parts.push(`Город: ${newAddress.city}`);
    if (newAddress.street) parts.push(`Улица: ${newAddress.street}`);
    if (newAddress.building) parts.push(`Дом: ${newAddress.building}`);
    if (newAddress.office) parts.push(`Офис: ${newAddress.office}`);

    newAddress.full = parts.join(", ");

    setForm((prev) => ({ ...prev, address: newAddress }));
  };

  const handleSubmit = async () => {
    const vatPayer = !!form.is_vat_payer;
    const today = new Date().toISOString().split("T")[0];

    const requiredFields = [
      { key: "type", label: "Тип компании" },
      { key: "name", label: "Название" },
      { key: "sn", label: "БИН / ИИН" },
      { key: "category_id", label: "Категория" },
      { key: "tax_regime", label: "Налоговый режим" },
      { key: "owner_name", label: "Имя владельца" },
      { key: "owner_surname", label: "Фамилия владельца" },
      { key: "owner_sn", label: "ИИН владельца" },
      { key: "owner_phone", label: "Телефон владельца" },
      { key: "owner_email", label: "Email владельца" },
      { key: "password", label: "Пароль" },
    ];

    const newErrors = {};
    const missingFields = [];

    requiredFields.forEach((field) => {
      if (!form[field.key] || form[field.key].toString().trim() === "") {
        newErrors[field.key] = true;
        missingFields.push(field.label);
      }
    });

    const addressFields = [
      { key: "city", label: "Город" },
      { key: "street", label: "Улица" },
      { key: "building", label: "Дом" },
    ];

    addressFields.forEach((field) => {
      if (
        !form.address[field.key] ||
        form.address[field.key].toString().trim() === ""
      ) {
        if (!newErrors.address) newErrors.address = {};
        newErrors.address[field.key] = true;
        missingFields.push(field.label);
      }
    });

    if (vatPayer) {
      if (!form.vat_series) {
        newErrors.vat_series = true;
        missingFields.push("Серия НДС");
      }
      if (!form.reg_number) {
        newErrors.reg_number = true;
        missingFields.push("Регистрационный номер");
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setToast(`Заполните поля: ${missingFields.join(", ")}`);
      setTimeout(() => setToast(""), 4000);

      // скролл к первому полю с ошибкой
      if (newErrors.address) {
        const firstAddressField = Object.keys(newErrors.address)[0];
        const el = document.querySelector(`[name="${firstAddressField}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        const firstField = Object.keys(newErrors)[0];
        const el = document.querySelector(`[name="${firstField}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const body = {
      type: form.type,
      name: form.name,
      address: {
        full: form.address.full,
        city: form.address.city,
        street: form.address.street,
        building: form.address.building,
        office: form.address.office,
      },
      sn: form.sn,
      category_id: Number(form.category_id),
      specialization_id: form.specialization_id
        ? Number(form.specialization_id)
        : null,
      status: "active",
      tax_regime: form.tax_regime || null,
      is_vat_payer: !!form.is_vat_payer,
      vat_series: form.is_vat_payer ? form.vat_series || null : null,
      reg_number: form.is_vat_payer ? form.reg_number || null : null,
      reg_date: today,
      owner_name: form.owner_name,
      owner_surname: form.owner_surname,
      owner_patronymic: form.owner_patronymic || null,
      owner_sn: form.owner_sn,
      owner_phone: form.owner_phone,
      password: form.password,
      owner_email: form.owner_email,
    };

    try {
      const res = await fetch(`${API_URL}/api/orgs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let errorText = "Ошибка сервера";

        try {
          const err = await res.json();
          errorText =
            err?.error ||
            err?.message ||
            (Array.isArray(err?.errors)
              ? err.errors.map((e) => e.message).join(", ")
              : "") ||
            JSON.stringify(err);
        } catch {
          errorText = await res.text();
        }

        console.error("Ошибка сервера:", errorText);
        setToast(errorText);
        setTimeout(() => setToast(""), 3000);
        return;
      }

      const data = await res.json();

      // если тебе нужно обновить список без reload:
      // onCreated?.(data);
      // resetForm();
      // onClose();

      onCreated?.(data);
      resetForm();
      onClose();
      // если прям надо как у тебя было:
      // window.location.reload();
    } catch (error) {
      console.error("Ошибка сети:", error);
      setToast(error.message || "Ошибка сети");
      setTimeout(() => setToast(""), 3000);
    }
  };

  // маленький хак: ставим readOnly, снимаем при фокусе — часто мешает автозаполнению
  const antiAutofillProps = {
    readOnly: true,
    onFocus: (e) => e.target.removeAttribute("readonly"),
    onBlur: (e) => e.target.setAttribute("readonly", "true"),
  };

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={() => { resetForm(); onClose(); }}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Создать компанию</h2>
          <button
            className={styles.cancel}
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            х
          </button>
        </div>

        {/* Важно: form с autoComplete off */}
        <form
          className={styles.body}
          autoComplete="off"
          // иногда помогает, когда браузер игнорит просто "off"
          data-autocomplete={ac.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Скрытые "ловушки" для автозаполнения (очень помогает в Chrome) */}
          <div style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
            <input type="text" name="fake-username" autoComplete="username" />
            <input type="password" name="fake-password" autoComplete="current-password" />
          </div>

          <h3 className={styles.blockTitle}>Основная информация</h3>
          <div className={styles.grid}>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              autoComplete={ac.type}
              className={`${styles.input} ${errors.type ? styles.errorInput : ""}`}
            >
              <option value="">Тип компании</option>
              <option value="individual">ИП</option>
              <option value="llp">ТОО</option>
              <option value="jsc">АО</option>
            </select>

            <input
              name="name"
              placeholder="Название"
              value={form.name}
              onChange={handleChange}
              autoComplete={ac.name}
              className={`${styles.input} ${errors.name ? styles.errorInput : ""}`}
              {...antiAutofillProps}
            />

            <input
              name="sn"
              placeholder="БИН / ИИН"
              value={form.sn}
              onChange={handleChange}
              autoComplete={ac.sn}
              className={`${styles.input} ${errors.sn ? styles.errorInput : ""}`}
              {...antiAutofillProps}
            />

            <select
              name="category_id"
              value={form.category_id}
              onChange={handleChange}
              autoComplete={ac.category}
              className={`${styles.input} ${
                errors.category_id ? styles.errorInput : ""
              }`}
            >
              <option value="">Сфера деятельности</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.title}
                </option>
              ))}
            </select>

            {form.category_id && (
              <select
                name="specialization_id"
                value={form.specialization_id}
                onChange={handleChange}
                autoComplete={ac.spec}
                className={styles.input}
              >
                <option value="">Специализация</option>
                {specializations.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          <h3 className={styles.blockTitle}>Адрес</h3>
          <div className={styles.grid}>
            <input
              name="city"
              type="text"
              placeholder="Город"
              value={form.address.city}
              onChange={handleAddressChange}
              autoComplete={ac.city}
              className={`${styles.input} ${
                errors.address && !form.address.city ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="street"
              type="text"
              placeholder="Улица"
              value={form.address.street}
              onChange={handleAddressChange}
              autoComplete={ac.street}
              className={`${styles.input} ${
                errors.address && !form.address.street ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="building"
              type="number"
              placeholder="Дом"
              value={form.address.building}
              onChange={handleAddressChange}
              autoComplete={ac.building}
              className={`${styles.input} ${
                errors.address && !form.address.building ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="office"
              placeholder="Офис (необязательно)"
              value={form.address.office}
              onChange={handleAddressChange}
              autoComplete={ac.office}
              className={styles.input}
              {...antiAutofillProps}
            />
          </div>

          <input
            disabled
            value={form.address.full}
            className={styles.input}
            placeholder="Полный адрес"
            style={{ opacity: 0.7 }}
            autoComplete="off"
          />

          <h3 className={styles.blockTitle}>Регистрационные данные</h3>
          <div className={styles.grid}>
            <select
              name="tax_regime"
              value={form.tax_regime}
              onChange={handleChange}
              autoComplete={ac.tax}
              className={`${styles.input} ${
                errors.tax_regime ? styles.errorInput : ""
              }`}
            >
              <option value="">Налоговый режим</option>
              <option value="common">Общеустановленный</option>
              <option value="simplified">Упрощённый</option>
              <option value="patent">Патент</option>
              <option value="kfh">КФХ</option>
              <option value="sales_tax">Налог с продаж</option>
            </select>

            <select
              name="is_vat_payer"
              value={form.is_vat_payer === "" ? "" : String(form.is_vat_payer)}
              onChange={handleChange}
              autoComplete={ac.vat}
              className={styles.input}
            >
              <option value="">Плательщик НДС?</option>
              <option value="true">12 %</option>
              <option value="false">Без НДС</option>
            </select>

            {form.is_vat_payer === true && (
              <>
                <input
                  name="vat_series"
                  placeholder="Серия НДС"
                  value={form.vat_series}
                  onChange={handleChange}
                  autoComplete={ac.vat_series}
                  className={`${styles.input} ${
                    errors.vat_series ? styles.errorInput : ""
                  }`}
                  {...antiAutofillProps}
                />

                <input
                  name="reg_number"
                  placeholder="Регистрационный номер"
                  value={form.reg_number}
                  onChange={handleChange}
                  autoComplete={ac.reg_number}
                  className={`${styles.input} ${
                    errors.reg_number ? styles.errorInput : ""
                  }`}
                  {...antiAutofillProps}
                />
              </>
            )}
          </div>

          <h3 className={styles.blockTitle}>Владелец</h3>
          <div className={styles.grid}>
            <input
              name="owner_name"
              placeholder="Имя"
              value={form.owner_name}
              onChange={handleChange}
              autoComplete={ac.owner_name}
              className={`${styles.input} ${
                errors.owner_name ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="owner_surname"
              placeholder="Фамилия"
              value={form.owner_surname}
              onChange={handleChange}
              autoComplete={ac.owner_surname}
              className={`${styles.input} ${
                errors.owner_surname ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="owner_patronymic"
              placeholder="Отчество"
              value={form.owner_patronymic}
              onChange={handleChange}
              autoComplete={ac.owner_patronymic}
              className={`${styles.input} ${
                errors.owner_patronymic ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="owner_sn"
              placeholder="ИИН владельца"
              value={form.owner_sn}
              onChange={handleChange}
              autoComplete={ac.owner_sn}
              className={`${styles.input} ${
                errors.owner_sn ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="owner_phone"
              placeholder="Телефон владельца"
              value={form.owner_phone}
              onChange={handleChange}
              autoComplete={ac.owner_phone}
              className={`${styles.input} ${
                errors.owner_phone ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="owner_email"
              type="email"
              placeholder="Email владельца"
              value={form.owner_email}
              onChange={handleChange}
              autoComplete={ac.owner_email}
              className={`${styles.input} ${
                errors.owner_email ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />

            <input
              name="password"
              type="password"
              placeholder="Пароль"
              value={form.password}
              onChange={handleChange}
              autoComplete={ac.password} // new-password
              className={`${styles.input} ${
                errors.password ? styles.errorInput : ""
              }`}
              {...antiAutofillProps}
            />
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancel}
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              Отмена
            </button>

            <button type="submit" className={styles.create}>
              Создать
            </button>
          </div>

          {toast && <div className={styles.toast}>{toast}</div>}
        </form>
      </div>
    </div>
  );
}
